// components/ClientFaqAccordion.js
"use client";

import React, { useState, useRef } from "react";
import FaqIcon from "../../../../assets/svg/FaqIcon";
import { useDictionary } from "../../../../context/dictionaryProvider";
import { ChevronDownIcon, ChevronUpIcon } from "../../../../assets/svg";
import { ContentContainer, Layout, Stack } from "../../../../components/molecules";

// Reuse renderTextFromRichContent here if needed
function renderTextFromRichContent(text = "") {
  // Split text into parts that can include bold (**text**) or links [text](url)
  const parts = text
    .split(/(\*\*.*?\*\*|\[.*?\]\(.*?\)|\n)/g)
    .filter(Boolean);

  return parts.map((part, idx) => {
    // ✅ Bold (**text**)
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={idx} className="font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }

    // ✅ Link [text](url)
    if (part.match(/^\[.*?\]\(.*?\)$/)) {
      const match = part.match(/^\[(.*?)\]\((.*?)\)$/);
      const linkText = match?.[1];
      const href = match?.[2];
      return (
        <a
          key={idx}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#0096AC] underline hover:text-[#007d8f]"
        >
          {linkText}
        </a>
      );
    }

    // ✅ New line → <br />
    if (part === "\n") return <br key={idx} />;

    // ✅ Regular text
    return <React.Fragment key={idx}>{part}</React.Fragment>;
  });
}



const AccordionItem = ({ title, description, isOpen, onClick }) => {
  const contentRef = useRef(null);
  return (
    <div className="transform transition-all duration-1000 ease-linear border-b-1 border-b-[#DFDFDF] text-start">
      <button
        className="flex justify-between items-center w-full py-4 font-semibold text-base text-start"
        onClick={onClick}
        aria-expanded={isOpen}
      >
        <h3>{title}</h3>
        {isOpen ? (
          <ChevronUpIcon className="size-6 stroke-[#a1a1aa]" />
        ) : (
          <ChevronDownIcon className="size-6 stroke-[#a1a1aa]" />
        )}
      </button>
      <div
        ref={contentRef}
        style={{
          maxHeight: isOpen ? `${contentRef.current?.scrollHeight}px` : "0px",
        }}
        className={`overflow-hidden transition-[max-height] duration-500 ease-in-out`}
      >
        {description && renderTextFromRichContent(description)}
      </div>
    </div>
  );
};

export default function ClientFaqAccordion({ title, dataList }) {
  const dict = useDictionary();
  const [expandedIndex, setExpandedIndex] = useState(null);

  const handleToggle = (index) => {
    setExpandedIndex(index === expandedIndex ? null : index);
  };

  const handleLoadMore = () => {
    setExpandedIndex(!expandedIndex);
  };

  return (
    <Layout className="">
      <ContentContainer>
        <Stack className="flex items-center gap-4 mx-auto mb-20" style={{ flexDirection: "column" }}>
          <FaqIcon />
          <h2 className="text-3xl lg:text-[40px] font-extrabold leading-normal">
            {title || dict?.faqs?.title}
          </h2>
        </Stack>

        <div className={`overflow-hidden h-full transform transition-max-height ease-linear duration-500 
            ${
              expandedIndex
                ? "overflow-y-auto max-h-full ease-in"
                : "overflow-hidden max-h-[32rem] ease-out"
            }`}
        >
          {dataList?.map((item, index) => (
            <AccordionItem
              key={index}
              title={item.title}
              description={item.description}
              isOpen={index === expandedIndex}
              onClick={() => handleToggle(index)}
            />
          ))}
        </div>

        <div className="flex justify-center">
          <button
            onClick={handleLoadMore}
            className="primaryLinkTxt flex gap-4 items-center justify-center mt-6 underline underline-offset-4 text-[#0096AC] bg-transparent"
          >
            {!expandedIndex && dataList.length > 4
              ? dict?.readMore
              : dict?.readLess}
          </button>
        </div>
      </ContentContainer>
    </Layout>
    
  );
}
